import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, User, Loader2 } from 'lucide-react';
import { blogArticles } from '../data/blogArticles';
import { Button } from '../components/ui/button';
import { renderArticleHTML, sanitizeHTML } from '../lib/markdown';
import { API_URL } from '../api/apiUrl';
import { ShareButtons } from '../components/ShareButtons';

const resolveImg = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [isCms, setIsCms] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    // Try the CMS first, then fall back to the static bundled articles.
    axios
      .get(`${API_URL}/api/blog/posts/${slug}`)
      .then(({ data }) => {
        if (!active) return;
        setArticle({
          ...data,
          authorTitle: data.author_title,
          featuredImage: resolveImg(data.featured_image),
        });
        setIsCms(true);
      })
      .catch(() => {
        if (!active) return;
        const staticArticle = blogArticles[slug];
        setArticle(staticArticle || null);
        setIsCms(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-notaria-secondary" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Artículo no encontrado</h1>
          <Link to="/blog">
            <Button className="bg-notaria-secondary hover:bg-notaria-primary text-white">
              Volver al Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const contentHTML = isCms ? sanitizeHTML(article.content) : renderArticleHTML(article.content);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Breadcrumb */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-notaria-secondary">Home</Link>
            <span>›</span>
            <Link to="/blog" className="hover:text-notaria-secondary">Blog</Link>
            <span>›</span>
            <span className="text-gray-900">{article.title}</span>
          </div>
        </div>
      </section>

      {/* Article Header */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-notaria-secondary hover:text-notaria-secondary mb-8">
              <ArrowLeft className="w-4 h-4" />
              Volver al Blog
            </Link>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-5 h-5" />
                <span>{article.date}</span>
              </div>
              {article.author && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-5 h-5" />
                  <div>
                    <span className="font-medium">{article.author}</span>
                    {article.authorTitle && (
                      <span className="block text-sm text-gray-500">{article.authorTitle}</span>
                    )}
                  </div>
                </div>
              )}
              <div className="inline-block bg-gray-100 text-notaria-secondary px-4 py-1 rounded-full text-sm font-semibold">
                {article.category}
              </div>
            </div>

            {/* Share (top) */}
            <div className="mb-10">
              <ShareButtons title={article.title} />
            </div>

            {/* Featured Image */}
            {article.featuredImage && (
              <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={article.featuredImage}
                  alt={article.title}
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div
                className="article-content text-gray-700 leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: contentHTML }}
              />
            </div>

            {/* Share (bottom) */}
            <div className="mt-12 pt-8 border-t">
              <ShareButtons title={article.title} />
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;
